import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/method", label: "Method" },
  { to: "/experiences", label: "Experiences" },
  { to: "/balanced-leader", label: "Balanced Leader" },
  { to: "/events", label: "Events" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 lg:px-20 h-20 max-w-7xl mx-auto">
        <Link to="/" data-testid="nav-logo" className="font-display font-bold text-xl tracking-tight text-bone">
          David <span className="text-brand">Barnes</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[11px] font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:text-brand ${
                location.pathname === l.to ? "text-brand" : "text-slate-300"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/apply"
            data-testid="nav-cta-apply"
            className="group inline-flex items-center gap-2 rounded-full bg-bone text-ink px-6 py-3 text-xs font-medium uppercase tracking-widest transition-colors duration-300 hover:bg-brand"
          >
            Start Your Journey
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-bone p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-black/90 border-b border-white/10"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              <Link to="/" data-testid="nav-mobile-link-home" className="font-display font-semibold text-3xl text-bone hover:text-brand transition-colors duration-300">Home</Link>
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display font-semibold text-3xl text-bone hover:text-brand transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/apply"
                data-testid="nav-mobile-cta-apply"
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-bone text-ink px-6 py-3 text-xs font-medium uppercase tracking-widest"
              >
                Start Your Journey <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
